import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DOCUMENT, ViewEncapsulation, inject, output, input, model } from '@angular/core';

import { ApContentModule } from '@ap/content';
import { ApIconComponent } from '@ap/icon';

import { ApSelectorOption } from '../../selector.modal';
import { ApSelectorModalGroup } from '../selector-modal.model';

@Component({
    selector: 'ap-selector-modal-single',
    templateUrl: './selector-modal-single.component.html',
    styleUrls: ['../selector-modal.generic.scss', './selector-modal-single.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, NgTemplateOutlet, ApContentModule, ApIconComponent]
})
export class ApSelectorModalSingleComponent {
    private readonly document = inject<Document>(DOCUMENT);

    /**
     * Array of Selector options
     */
    public readonly options = input<ApSelectorOption[]>();

    /**
     * Currently selected option
     */
    public readonly selectedOption = model<ApSelectorOption>();

    /**
     * Optional grouping of items
     */
    public readonly groups = input<ApSelectorModalGroup[]>();

    /**
     * Should icons be shown?
     */
    public readonly showIcons = input(false);

    /**
     * Title of the listbox
     */
    public readonly title = input<string>();

    /**
     * This function will trigger when a option is selected
     */
    public readonly selected = output<ApSelectorOption>();

    /**
     * If provided, this function will trigger when the item is closed
     */
    public readonly openSubModal = output<ApSelectorOption>();

    /**
     * Get filtered options of group
     */
    protected getGroupOptions(groupId: number): ApSelectorOption[] {
        return this.options().filter((option) => option.groupId === groupId);
    }

    /**
     * Select an option and close modal
     */
    protected select(option: ApSelectorOption): void {
        if (option.subOptions) {
            this.openSubModal.emit(option);
            return;
        }
        this.selectedOption.set(option);
        this.selected.emit(option);
    }

    /**
     * On focus of listbox, if no option selected, select the first
     */
    protected listboxFocus(): void {
        const options = this.options();
        if (this.selectedOption() || !options) {
            return;
        }
        this.selectedOption.set(options[0] || null);
    }

    /**
     * Capture click of listbox
     */
    protected listboxClick(event: MouseEvent): void {
        const selectedOptionId = (event.target as HTMLElement).getAttribute('data-id');
        const selectedOption = this.options().find((option: ApSelectorOption) => option.id === selectedOptionId);
        this.select(selectedOption);
    }

    /**
     * Capture different event keys of listbox
     */
    protected listboxKeyDown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Enter':
                event.stopPropagation();
                event.preventDefault();
                this.select(this.selectedOption());
                break;
            case 'Home':
                this.selectFirstItem();
                event.preventDefault();
                break;
            case 'End':
                this.selectLastItem();
                event.preventDefault();
                break;
            case 'ArrowDown':
                this.selectNextItem();
                event.preventDefault();
                break;
            case 'ArrowUp':
                this.selectPreviousItem();
                event.preventDefault();
                break;
            default:
                break;
        }
    }

    /**
     * Check if passed option is selected
     */
    protected isSelected(option: ApSelectorOption): boolean {
        const selectedOption = this.selectedOption();
        if (!selectedOption) {
            return false;
        }
        return selectedOption.id === option.id;
    }

    /**
     * Select the first option available
     */
    private selectFirstItem(): void {
        this.selectedOption.set(this.options()[0]);
        this.scrollToSelectedOption();
    }

    /**
     * Select the last option available
     */
    private selectLastItem(): void {
        this.selectedOption.set(this.options().at(-1));
        this.scrollToSelectedOption();
    }

    /**
     * Select the next option available
     */
    private selectNextItem(): void {
        const optionToSelect = this.options()[this.indexOfSelectedOption + 1];

        if (!optionToSelect) {
            return;
        }

        this.selectedOption.set(optionToSelect);
        this.scrollToSelectedOption();
    }

    /**
     * Select the previous option available
     */
    private selectPreviousItem(): void {
        const optionToSelect = this.options()[this.indexOfSelectedOption - 1];

        if (!optionToSelect) {
            return;
        }
        this.selectedOption.set(optionToSelect);
        this.scrollToSelectedOption();
    }

    /**
     * Scroll element into view after selecting
     */
    private scrollToSelectedOption(): void {
        const selectedOptionEl = this.document.getElementById(this.selectedOption()?.id);

        if (!selectedOptionEl) {
            return;
        }
        selectedOptionEl.scrollIntoView();
    }

    /**
     * Get index of selected option in array
     */
    private get indexOfSelectedOption(): number {
        if (!this.selectedOption()) {
            return null;
        }
        return this.options().findIndex((option) => this.selectedOption().id === option.id);
    }
}
